import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileUp, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

const STAGES = ["Identified", "Qualifying", "Proposal In Progress", "Submitted", "Won", "Lost"];

export default function ImportSheet({ onImported, onClose }) {
  const fileRef = useRef();
  const [phase, setPhase] = useState("pick");
  const [items, setItems] = useState([]);
  const [filename, setFilename] = useState("");
  const [included, setIncluded] = useState([]);

  const parse = async (file) => {
    if (!file) return;
    setPhase("parsing");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/parse", fd, { timeout: 180000 });
      setItems(data.items);
      setIncluded(data.items.map(() => true));
      setFilename(data.filename);
      setPhase("review");
    } catch (e) {
      toast.error(apiError(e));
      setPhase("pick");
    }
  };

  const setField = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    setItems(next);
  };

  const confirm = async () => {
    const selected = items.filter((_, i) => included[i]);
    if (selected.length === 0) return toast.error("Nothing selected");
    setPhase("saving");
    try {
      const { data } = await api.post("/import/confirm", { items: selected, filename });
      toast.success(`${data.inserted} opportunit${data.inserted === 1 ? "y" : "ies"} added to pipeline`);
      onImported();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
      setPhase("review");
    }
  };

  if (phase === "pick" || phase === "parsing") {
    return (
      <div className="py-6 space-y-4">
        <p className="text-xs text-muted-foreground">Upload a pipeline spreadsheet, RFP, solicitation, or notes doc (PDF, DOCX, XLSX, CSV, TXT). AI extracts the opportunities — you review every row before anything is added.</p>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.csv,.txt,.md" className="hidden"
          data-testid="import-file-input" onChange={(e) => parse(e.target.files[0])} />
        <button
          data-testid="import-pick-file-button"
          disabled={phase === "parsing"}
          onClick={() => fileRef.current.click()}
          className="w-full h-24 border border-dashed border-primary/60 text-primary flex flex-col items-center justify-center gap-2 tap-btn disabled:opacity-60"
        >
          {phase === "parsing" ? (
            <><Loader2 size={20} className="animate-spin" /><span className="text-xs font-bold uppercase tracking-widest">AI parsing document...</span></>
          ) : (
            <><FileUp size={20} /><span className="text-xs font-bold uppercase tracking-widest">Choose file</span></>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-3 pb-8">
      <p className="text-xs text-muted-foreground"><span className="font-mono text-primary">{items.length}</span> opportunities found in <span className="font-mono">{filename}</span>. Uncheck any you don't want, edit inline, then confirm.</p>
      <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
        {items.map((it, i) => (
          <div key={i} data-testid={`import-item-${i}`} className={`border p-3 space-y-2 ${included[i] ? "border-border bg-card" : "border-border/40 opacity-50"}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" data-testid={`import-include-${i}`} checked={included[i]}
                onChange={() => setIncluded(included.map((v, j) => (j === i ? !v : v)))}
                className="w-5 h-5 accent-[#0E5C5A] shrink-0" />
              <input className="tactical-input h-9 text-xs font-bold" value={it.client_name}
                data-testid={`import-client-${i}`} onChange={(e) => setField(i, "client_name", e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <select className="tactical-input h-9 text-[11px] px-1.5" value={it.stage} onChange={(e) => setField(i, "stage", e.target.value)}>
                {STAGES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <input type="number" className="tactical-input h-9 text-[11px] px-1.5" placeholder="Value $" value={it.value ?? ""}
                onChange={(e) => setField(i, "value", e.target.value === "" ? null : Number(e.target.value))} />
              <input type="date" className="tactical-input h-9 text-[11px] px-1.5" value={it.due_date || ""}
                onChange={(e) => setField(i, "due_date", e.target.value || null)} />
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-1.5">
              <input className="tactical-input h-9 text-[11px]" value={it.next_action}
                onChange={(e) => setField(i, "next_action", e.target.value)} />
              <input type="date" className="tactical-input h-9 text-[11px] px-1.5 w-32" value={it.next_action_date}
                onChange={(e) => setField(i, "next_action_date", e.target.value)} />
            </div>
            {it.notes && <p className="text-[10px] text-muted-foreground line-clamp-2">{it.notes}</p>}
          </div>
        ))}
      </div>
      <button
        data-testid="import-confirm-button"
        disabled={phase === "saving"}
        onClick={confirm}
        className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm tap-btn disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Upload size={14} />{phase === "saving" ? "Adding..." : `Add ${included.filter(Boolean).length} to pipeline`}
      </button>
    </div>
  );
}
